import { getUserInfoRequest } from "_data/services/auth/service";
import {
  getBillingAddressRequest,
  setBillingAddressRequest,
  unsubscribePaymentRequest,
  initiatePaymentSubscriptionRequest,
  abandonOrderRequest,
  getOrderStateRequest,
  switchPaymentSubscriptionRequest,
  getBillingHistoryRequest,
  getSubscriptionTypesRequest,
} from "_data/services/payment/service";
import {
  isPremium,
  SubscriptionStatus,
} from "__utilities/functions/subscription";
import {
  clearPendingOrderId,
  setPendingOrderId,
} from "__utilities/functions/sessionHandler";

/**
 * How long to keep asking whether a payment has been recorded.
 *
 * The customer's browser coming back from the provider does not mean the payment is
 * recorded -- that is confirmed by a webhook on a separate connection, which usually lands
 * within a second or two but can be later. So we poll, quickly at first and then more
 * slowly, and when we run out of patience we say the payment is being processed. We never
 * say it failed: telling a customer their payment failed after a successful charge makes
 * them pay twice or phone support.
 */
const POLL_TIMEOUT_MS = 60000;
const POLL_FAST_INTERVAL_MS = 2000;
const POLL_SLOW_INTERVAL_MS = 5000;
const POLL_FAST_ATTEMPTS = 5;

export const PollResult = {
  Confirmed: "Confirmed",
  Timeout: "Timeout",
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function usePayment() {
  const getUserInfoData = (callback = () => {}) => {
    getUserInfoRequest((success, response) => {
      callback(success, response);
    });
  };

  const getInitialBillingAddress = (callback = () => {}) => {
    getBillingAddressRequest((success, response) => {
      callback(success, response);
    });
  };

  const sendBillingAddress = (body, callback) => {
    setBillingAddressRequest(body, (success, response) => {
      callback(success, response);
    });
  };

  const getBillingHistory = (callback = () => {}) => {
    getBillingHistoryRequest((success, response) => {
      callback(success, response);
    });
  };

  const getSubscriptionTypes = (callback = () => {}) => {
    getSubscriptionTypesRequest((success, response) => {
      callback(success, response);
    });
  };

  /**
   * Reads the current state of an order. Read-only: arriving back from the provider grants
   * nothing, so this reports what the webhook has recorded so far and nothing more.
   */
  const getOrderState = (orderId, callback = () => {}) => {
    if (!orderId) {
      callback(false, null);
      return;
    }

    getOrderStateRequest(orderId, (success, response) => {
      callback(success, response);
    });
  };

  const requestUserInfo = () =>
    new Promise((resolve) => {
      getUserInfoRequest((success, response) =>
        resolve(success ? response : null),
      );
    });

  const requestOrderState = (orderId) =>
    new Promise((resolve) => {
      getOrderStateRequest(orderId, (success, response) =>
        resolve(success ? response : null),
      );
    });

  /**
   * Asks the backend, repeatedly, whether the licence has been granted yet.
   *
   * Entitlement is executionLicense and nothing else, so that is what we wait for -- not the
   * subscription status, which can be Active while the first payment is still unrecorded.
   *
   * Resolves `{ result, orderRecorded }`. Timeout is not an error and must not be presented as
   * one; `orderRecorded` says whether the order itself reached Active, which lets a caller that
   * ran out of patience say "your payment is recorded, the account is catching up" instead of
   * the vaguer "it is being processed".
   */
  const pollUntilPremium = async (
    orderId,
    { onUserInfo = () => {}, isCancelled = () => false } = {},
  ) => {
    const startedAt = Date.now();
    let attempt = 0;
    let orderRecorded = false;

    const timedOut = () => ({ result: PollResult.Timeout, orderRecorded });

    while (!isCancelled()) {
      // Read-only state check. Any failure is ignored on purpose: a payment the webhook has
      // not confirmed yet is the normal case here, not a problem to report to the customer.
      //
      // Only asked until it says yes. Once the order is recorded the answer cannot change back,
      // and this only exists to choose between two timeout messages -- so continuing to ask
      // would double the request count on the slow path to learn nothing.
      if (orderId && orderRecorded === false) {
        const orderState = await requestOrderState(orderId);
        if (orderState?.status === SubscriptionStatus.Active) {
          orderRecorded = true;
        }
      }
      if (isCancelled()) {
        return timedOut();
      }

      const userInfo = await requestUserInfo();
      if (isCancelled()) {
        return timedOut();
      }
      if (userInfo) {
        onUserInfo(userInfo);
        if (isPremium(userInfo)) {
          clearPendingOrderId();
          return { result: PollResult.Confirmed, orderRecorded: true };
        }
      }

      attempt += 1;
      const delay =
        attempt <= POLL_FAST_ATTEMPTS
          ? POLL_FAST_INTERVAL_MS
          : POLL_SLOW_INTERVAL_MS;

      if (Date.now() - startedAt + delay >= POLL_TIMEOUT_MS) {
        return timedOut();
      }

      await wait(delay);
    }

    return timedOut();
  };

  /**
   * Subscribes, or resumes a subscription that was set to end -- the backend works out which
   * from the user's state. This does **not** change plan: on a live subscription for the other
   * plan the call is refused with USER_ALREADY_SUBSCRIBED, and `switchPayment` is the one that
   * changes plan.
   *
   * The callback reports (false, errorCode) when the call was rejected, or (true) when the new
   * state is in effect and the caller should re-render.
   *
   * Every genuinely new subscription needs the provider's hosted payment page, including for
   * customers who have paid before -- there is no path that charges a stored card without a
   * redirect. The redirect must be full-page: the provider forbids framing, so an iframe or
   * modal renders blank.
   *
   * When paymentNeeded is false the user is already Premium, so there is nothing to poll for.
   * It is only returned for an existing Active subscription -- already on this plan, or just
   * resumed -- and Active is only ever reached after a paid invoice granted the licence.
   * Polling belongs on the success page alone, where a redirect really did just happen.
   */
  const initiatePayment = (type, callback = () => {}) => {
    initiatePaymentSubscriptionRequest(type, (success, response) => {
      if (!success) {
        callback(false, response);
        return;
      }

      if (response?.paymentNeeded) {
        if (!response?.paymentUrl) {
          callback(false, null);
          return;
        }
        // Persist before leaving: the return pages have to work with no in-memory state.
        setPendingOrderId(response?.orderId);
        window.location.assign(response.paymentUrl);
        return;
      }

      clearPendingOrderId();
      callback(true, null);
    });
  };

  /**
   * Changes the plan on a live subscription. Takes effect at the next renewal: nothing is
   * charged now and the difference is not refunded.
   *
   * Refused with SUBSCRIPTION_CANCELLATION_PENDING when the subscription is already set to end,
   * because it would end before a repriced item ever renewed -- the customer would see the new
   * plan everywhere except on the invoice that never arrives. Resume first; resuming can change
   * the plan in the same step.
   */
  const switchPayment = (type, callback = () => {}) => {
    switchPaymentSubscriptionRequest(type, (success, errorCode) => {
      callback(success, errorCode);
    });
  };

  const unsubscribePayment = (callback = () => {}) => {
    unsubscribePaymentRequest((success, response) => {
      callback(success, response);
    });
  };

  /**
   * Abandons an order the customer walked away from. Cannot end a live subscription -- that is
   * `unsubscribePayment`. Takes the order id explicitly, because the return page may have been
   * opened cold and read it from the session rather than the route.
   */
  const abandonOrder = (orderId, callback = () => {}) => {
    if (!orderId) {
      callback(false, null);
      return;
    }

    abandonOrderRequest(orderId, (success, response) => {
      clearPendingOrderId();
      callback(success, response);
    });
  };

  return {
    getUserInfoData,
    getInitialBillingAddress,
    sendBillingAddress,
    getBillingHistory,
    getSubscriptionTypes,
    getOrderState,
    initiatePayment,
    switchPayment,
    unsubscribePayment,
    pollUntilPremium,
    abandonOrder,
  };
}
